import {useMediaQuery} from '@chakra-ui/react';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiImage,
  UiInput,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
} from '~/components/elements';
import {AppCarousel} from '~/components/elements/AppCarousel';
import {UiCard} from '~/components/elements/UiCard';
import {QUERY_MOBILE} from '~/constants';
import {STI_TESTING_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {Option, Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';
import MapPlaces from '~/components/shared/MapPlaces';
import {average} from '~/utils/helpers';
import StylesMap from '~/pages/homes/sti-testing/StylesMap';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';

const AT_A_PARTNER_CLINIC = 'AT_A_PARTNER_CLINIC';
const A_PLACE_OF_MY_CHOICE = 'A_PLACE_OF_MY_CHOICE';

const Questionnaire6Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const {addMessage} = useNotification(dispatch);
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[STI_TESTING_KEYS.SAMPLE_COLLECTION_LOCATION]?.data,
  );

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === STI_TESTING_KEYS.SAMPLE_COLLECTION_LOCATION);

  let medicalOptions: Option[] = [];
  if (questionnaire?.options?.length) {
    medicalOptions = questionnaire?.options?.filter(i => i?.value !== 'NONE');
  }

  const rules = {
    address: {
      presence: {allowEmpty: false},
    },
    unitNumber: {
      presence: {allowEmpty: false},
    },
    postalCode: {
      presence: {allowEmpty: false},
    },
    specialInstruction: {
      presence: {allowEmpty: false},
    },
  };

  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      selectedOption: currentAnswer?.selectedOption || '',
      address: currentAnswer?.address || '',
      unitNumber: currentAnswer?.unitNumber || '',
      postalCode: currentAnswer?.postalCode || '',
      specialInstruction: currentAnswer?.specialInstruction || '',
      clinic: currentAnswer?.clinic || '',
    },
    {rules},
  );

  const checkPartner = inputs.selectedOption.value === AT_A_PARTNER_CLINIC && !!inputs.clinic.value;
  const checkPlace =
    inputs.selectedOption.value === A_PLACE_OF_MY_CHOICE &&
    !!inputs.address &&
    !!inputs.unitNumber &&
    !!inputs.postalCode &&
    !!inputs.specialInstruction;

  const onClickOption = (answer: Option) => {
    setInputs({selectedOption: answer});
  };

  const onClickSubmit = async () => {
    if (inputs.selectedOption.value === A_PLACE_OF_MY_CHOICE) {
      if (validation.getErrors()) {
        return;
      }
    }
    dispatch(
      setQuestionnaireAnswer({
        key: STI_TESTING_KEYS.SAMPLE_COLLECTION_LOCATION,
        index: questionnaire?.index || '',
        data: {...inputs, clinicId: inputs.clinic.value},
      }),
    );
    dispatch(setNextQuestionnaire({value: STI_TESTING_KEYS.CHOOSE_MEDICATION}));
  };

  const renderMyChoice = () => {
    if (inputs.selectedOption.value === A_PLACE_OF_MY_CHOICE) {
      return (
        <UiBox w="100%" mt={spacing.small} className="animate__animated animate__fadeInLeft">
          {inputs.selectedOption?.relations?.map((val: Relation) => (
            <UiBox key={val?.questions?.default}>
              <UiText fontSize="md" fontWeight="bold" mt={spacing.tiny}>
                {val?.questions?.default}
              </UiText>
              <UiCard variant="rounded" width="100%" mt={spacing.small} mb={spacing.medium}>
                <UiFormControl id="address" isRequired isInvalid={!!validation?.errors?.address}>
                  <UiFormLabel fontWeight="400" fontSize="xs">
                    Address
                  </UiFormLabel>
                  <UiInput
                    placeholder="Your Address..."
                    variant="flushed"
                    defaultValue={inputs.address}
                    onChange={({target}) => setInputs({address: target.value})}
                    fontWeight="600"
                    fontSize="sm"
                  />
                  {validation?.errors?.address && (
                    <UiFormErrorMessage>{validation?.errors?.address[0]}</UiFormErrorMessage>
                  )}
                </UiFormControl>
                <UiFormControl id="unitNumber" isRequired isInvalid={!!validation?.errors?.unitNumber}>
                  <UiFormLabel fontWeight="400" fontSize="xs">
                    Unit Number
                  </UiFormLabel>
                  <UiInput
                    placeholder="#04 - 88"
                    variant="flushed"
                    defaultValue={inputs.unitNumber}
                    onChange={({target}) => setInputs({unitNumber: target.value})}
                    fontWeight="600"
                    fontSize="sm"
                  />
                  {validation?.errors?.unitNumber && (
                    <UiFormErrorMessage>{validation?.errors?.unitNumber[0]}</UiFormErrorMessage>
                  )}
                </UiFormControl>
                <UiFormControl id="postalCode" isRequired isInvalid={!!validation?.errors?.postalCode}>
                  <UiFormLabel fontWeight="400" fontSize="xs">
                    Post Code
                  </UiFormLabel>
                  <UiInput
                    placeholder="099785"
                    variant="flushed"
                    defaultValue={inputs.postalCode}
                    onChange={({target}) => setInputs({postalCode: target.value})}
                    fontWeight="600"
                    fontSize="sm"
                  />
                  {validation?.errors?.postalCode && (
                    <UiFormErrorMessage>{validation?.errors?.postalCode[0]}</UiFormErrorMessage>
                  )}
                </UiFormControl>
                <UiFormControl id="specialInstruction" isRequired isInvalid={!!validation?.errors?.specialInstruction}>
                  <UiFormLabel fontWeight="400" fontSize="xs">
                    Special Instructions
                  </UiFormLabel>
                  <UiInput
                    placeholder="Call me before you arrive"
                    variant="flushed"
                    defaultValue={inputs.specialInstruction}
                    onChange={({target}) => setInputs({specialInstruction: target.value})}
                    fontWeight="600"
                    fontSize="sm"
                  />
                  {validation?.errors?.specialInstruction && (
                    <UiFormErrorMessage>{validation?.errors?.specialInstruction[0]}</UiFormErrorMessage>
                  )}
                </UiFormControl>
              </UiCard>
            </UiBox>
          ))}
        </UiBox>
      );
    }
    return null;
  };

  const renderPartnerClinic = () => {
    if (inputs.selectedOption.value === AT_A_PARTNER_CLINIC) {
      return (
        <UiBox w="100%" mt={spacing.small} className="animate__animated animate__fadeInLeft">
          {inputs.selectedOption?.relations?.map((val: Relation) => {
            const places = val?.options?.map(option => ({lat: option?.lat, lng: option?.lng}));
            const centerLat = val?.options?.map(option => option?.lat);
            const centerLng = val?.options?.map(option => option?.lng);
            return (
              <UiBox key={val?.questions?.default}>
                <UiText fontSize="md" fontWeight="bold" mt={spacing.tiny}>
                  {val?.questions?.default}
                </UiText>
                <UiBox>
                  <MapPlaces
                    path={places}
                    center={{
                      lat: inputs.clinic ? inputs.clinic.lat : average(centerLat),
                      lng: inputs.clinic ? inputs.clinic.lng : average(centerLng),
                    }}
                    styles={StylesMap}
                  />
                </UiBox>
                <UiBox mt={spacing.small}>
                  <AppCarousel
                    selectedItem={1}
                    showIndicators={false}
                    showArrows={true}
                    showStatus={false}
                    showThumbs={false}
                    centerMode
                    centerSlidePercentage={isDesktop ? 30 : 40}
                  >
                    {val?.options?.map((option: Option) => {
                      const isSelected = inputs.clinic.value === option.value;
                      return (
                        <UiBox
                          m={spacing.small}
                          key={option.name}
                          opacity={isSelected ? 1 : 0.5}
                          onClick={() => setInputs({clinic: option})}
                        >
                          <UiButton
                            variant="outline"
                            title={option.name}
                            borderRadius="40px"
                            borderWidth={isSelected ? 2 : 1}
                            px="30px"
                            _focus={{boxShadow: 'none'}}
                          />
                        </UiBox>
                      );
                    })}
                  </AppCarousel>
                </UiBox>
              </UiBox>
            );
          })}
        </UiBox>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!checkPlace && !checkPartner}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.small} />
        <UiText fontSize="lg" fontWeight="bold" mt={spacing.medium}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption.value === value.value ? 'solid' : 'toggle'}
                onClick={() => onClickOption(value)}
              />
            ))}
          </UiSimpleGrid>
        </UiBox>
        {renderMyChoice()}
        {renderPartnerClinic()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire6Screen;
